import type { AssetFlow, CurrencyCode, DayFlow } from './flow-types';
import { CURRENCIES, VIEWS } from './flows-constants';

/* ---------- Helpers ---------- */
export const parseDay = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};
export const fmtDate = (d: Date, o?: Intl.DateTimeFormatOptions): string => d.toLocaleDateString('en-GB', o ?? { month: 'short', day: 'numeric' });

export const money = (n: number, unit: CurrencyCode, decimals: number = 2): string => {
  const c = CURRENCIES[unit];
  const t = Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: decimals ?? c.dec, maximumFractionDigits: decimals ?? c.dec });
  return (n < 0 ? '−' : '') + c.sym + t;
};
export const short = (n: number, unit: CurrencyCode, decimals: number = 2): string => {
  const c = CURRENCIES[unit];
  const a = Math.abs(n);
  const s = a >= 1e6 ? `${+(a / 1e6).toFixed(1)}M` : a >= 1e3 ? `${Math.round(a / 1e3)}k'` : +a.toFixed(decimals ?? Math.max(c.dec, 1));
  return (n < 0 ? '−' : '') + c.sym + s;
};
export const createAggregatedFlow = (flows: AssetFlow[]): AssetFlow | null => {
  if (flows.length === 1) {
    return null;
  }
  const id = 'aggregate';
  const label = `Aggregate (${flows.map((f) => VIEWS[f.id]?.label).join(' + ')})`;
  const currencies = [...new Set(flows.map((f) => f.unit))];
  if (currencies.length > 1) {
    return null;
  }

  const byDate = flows
    .flatMap((f) => f.days)
    .reduce((acc, d) => {
      const cur = acc.get(d.date) ?? { date: d.date, incoming: 0, outgoing: 0 };
      acc.set(d.date, {
        ...cur,
        incoming: cur.incoming + d.incoming,
        outgoing: cur.outgoing + d.outgoing,
      });
      return acc;
    }, new Map<string, DayFlow>());

  return {
    id,
    label,
    unit: flows[0].unit,
    days: [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date)),
    pending: {
      incoming: flows.map((f) => f.pending.incoming).reduce((acc, v) => acc + v, 0),
      outgoing: flows.map((f) => f.pending.outgoing).reduce((acc, v) => acc + v, 0),
    },
  };
};
